import {
  forceSimulation,
  forceLink,
  forceManyBody,
  forceX,
  forceY,
  forceCollide,
} from 'd3-force';

// Configuration constants
const LINK_DISTANCE = 200;
const LINK_STRENGTH = 0.4;
const CHARGE_STRENGTH = -800;
const Y_STRENGTH = 0.2;
const X_STRENGTH = 0.03; // Weak centering force
const COLLISION_RADIUS = 80;
const GENERATION_HEIGHT = 350;
const SIMULATION_ITERATIONS = 500;

/**
 * Calculate generation levels relative to focal person using BFS
 * @param {Array} members - Array of member objects with relationship data
 * @param {number|null} focalPersonId - ID of focal person
 * @returns {Map} Map of memberId -> generation level (0 = focal, -1 = parents, +1 = children)
 */
function calculateGenerationLevels(members, focalPersonId) {
  const levels = new Map();
  const memberMap = new Map(members.map(m => [m.id, m]));

  if (!focalPersonId || !memberMap.has(focalPersonId)) {
    // No focal person - assign all to level 0
    members.forEach(m => levels.set(m.id, 0));
    return levels;
  }

  const visited = new Set();
  const queue = [[focalPersonId, 0]];

  while (queue.length > 0) {
    const [memberId, level] = queue.shift();
    if (visited.has(memberId)) continue;
    visited.add(memberId);
    levels.set(memberId, level);

    const member = memberMap.get(memberId);
    if (!member) continue;

    // Parents go up (level - 1)
    member.parents?.forEach(parentId => {
      if (!visited.has(parentId)) {
        queue.push([parentId, level - 1]);
      }
    });

    // Children go down (level + 1)
    member.children?.forEach(childId => {
      if (!visited.has(childId)) {
        queue.push([childId, level + 1]);
      }
    });

    // Siblings, spouses, co-parents stay on same level
    [...(member.siblings || []), ...(member.spouses || []), ...(member.coparents || [])].forEach(relatedId => {
      if (!visited.has(relatedId)) {
        queue.push([relatedId, level]);
      }
    });
  }

  // Handle any disconnected members (shouldn't happen but just in case)
  members.forEach(m => {
    if (!levels.has(m.id)) {
      levels.set(m.id, 0);
    }
  });

  return levels;
}

/**
 * Build links array for d3-force from member relationships
 * @param {Array} members - Array of member objects with relationship data
 * @returns {Array} Array of {source, target, type} objects
 */
function buildLinks(members) {
  const links = [];
  const addedPairs = new Set();

  const addLink = (id1, id2, type) => {
    const key = [id1, id2].sort().join('-');
    if (!addedPairs.has(key)) {
      addedPairs.add(key);
      links.push({ source: id1, target: id2, type });
    }
  };

  members.forEach(member => {
    // Parent-child links
    member.children?.forEach(childId => {
      addLink(member.id, childId, 'parent-child');
    });

    // Spouse links
    member.spouses?.forEach(spouseId => {
      addLink(member.id, spouseId, 'spouse');
    });

    // Sibling links
    member.siblings?.forEach(siblingId => {
      addLink(member.id, siblingId, 'sibling');
    });

    // Co-parent links
    member.coparents?.forEach(coparentId => {
      addLink(member.id, coparentId, 'co-parent');
    });
  });

  return links;
}

/**
 * Calculate positions using force-directed layout with vertical generation bias
 *
 * @param {Object} tree - Tree object from buildFamilyTrees() with members array
 * @param {Map} degreeMap - Map of memberId -> {degree, relationship} (used for styling, not layout)
 * @param {number|null} focalPersonId - ID of focal person (center of graph)
 * @returns {Map} Map of memberId -> {x, y}
 */
export function calculateForceLayout(tree, degreeMap = new Map(), focalPersonId = null) {
  const { members } = tree;
  const positions = new Map();

  // Handle empty or single-member trees
  if (!members || members.length === 0) {
    return positions;
  }

  if (members.length === 1) {
    positions.set(members[0].id, { x: 0, y: 0 });
    return positions;
  }

  // Calculate generation levels for Y positioning
  const generationLevels = calculateGenerationLevels(members, focalPersonId);

  // Build nodes array for d3-force
  // Initialize positions with some spread to help simulation converge
  const nodes = members.map((member, i) => {
    const level = generationLevels.get(member.id) || 0;
    return {
      id: member.id,
      index: i,
      // Initial position: spread horizontally, use target Y
      x: (i - members.length / 2) * 50,
      y: level * GENERATION_HEIGHT,
      targetY: level * GENERATION_HEIGHT,
    };
  });

  // Create id -> node index map for link references
  const nodeById = new Map(nodes.map((n, i) => [n.id, i]));

  // Build links array
  const links = buildLinks(members).map(link => ({
    source: nodeById.get(link.source),
    target: nodeById.get(link.target),
    type: link.type,
  })).filter(link => link.source !== undefined && link.target !== undefined);

  // Create force simulation
  const simulation = forceSimulation(nodes)
    // Links pull connected nodes together
    .force('link', forceLink(links)
      .distance(LINK_DISTANCE)
      .strength(LINK_STRENGTH)
    )
    // Nodes repel each other
    .force('charge', forceManyBody()
      .strength(CHARGE_STRENGTH)
    )
    // Pull toward generation Y level (vertical structure)
    .force('y', forceY(d => d.targetY)
      .strength(Y_STRENGTH)
    )
    // Weak horizontal centering
    .force('x', forceX(0)
      .strength(X_STRENGTH)
    )
    // Prevent node overlap
    .force('collide', forceCollide(COLLISION_RADIUS))
    // Stop automatic ticking - we'll run manually
    .stop();

  // Run simulation for fixed number of iterations
  for (let i = 0; i < SIMULATION_ITERATIONS; i++) {
    simulation.tick();
  }

  // Extract final positions
  nodes.forEach(node => {
    positions.set(node.id, { x: node.x, y: node.y });
  });

  // Center on focal person
  if (focalPersonId && positions.has(focalPersonId)) {
    const focalPos = positions.get(focalPersonId);
    const offsetX = focalPos.x;
    const offsetY = focalPos.y;

    positions.forEach((pos, id) => {
      positions.set(id, {
        x: pos.x - offsetX,
        y: pos.y - offsetY,
      });
    });
  }

  return positions;
}
